import { Pressable } from 'components/Pressable';
import { ScreenOverlay } from 'components/ScreenOverlay';
import { useSelector } from 'hooks/useSelector';
import { downscaleProportionally } from 'lib/scaleWidthHeight';
import { theme } from 'lib/theme';
import React from 'react';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { useDispatch } from 'react-redux';
import { doUnsetImagePreview } from 'store/actions';
import { hapticSelection } from 'lib/withHapticFeedback';
import { selectImagePreview } from 'store/app/selectors';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useEsc } from 'hooks/useEsc';
import { Image, ImageLoadEventData } from 'expo-image';

const WINDOW_MARGIN = 48;

export const ImageModal: React.FC = () => {
  const image = useSelector(selectImagePreview);
  const [loaded, setLoaded] = React.useState(false);
  const [dimensions, setDimensions] = React.useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });
  const dispatch = useDispatch();
  const closePreviewImage = React.useCallback(
    () => dispatch(doUnsetImagePreview()),
    [dispatch],
  );
  const { height: winHeight, width: winWidth } = useWindowDimensions();

  React.useEffect(() => {
    hapticSelection();
  }, [image]);

  const opacityRef = useSharedValue(0);

  const handleLoaded = React.useCallback((event: ImageLoadEventData) => {
    setDimensions({ width: event.source.width, height: event.source.height });
    setLoaded(true);
  }, []);

  React.useEffect(() => {
    if (!image) {
      setLoaded(false);
    }
  }, [opacityRef, image]);

  React.useEffect(() => {
    if (loaded) {
      opacityRef.value = withTiming(1, {
        duration: 500,
        easing: Easing.out(Easing.exp),
      });
    } else {
      opacityRef.value = 0;
    }
  }, [opacityRef, loaded]);

  const animatedStyles = useAnimatedStyle(() => {
    return {
      opacity: opacityRef.value,
    };
  });

  useEsc(!!image && closePreviewImage);

  if (!image) {
    return null;
  }

  const { height, width } = downscaleProportionally(
    dimensions.width,
    dimensions.height,
    winWidth - WINDOW_MARGIN,
    winHeight - WINDOW_MARGIN,
  );

  return (
    <ScreenOverlay>
      <Animated.View style={[StyleSheet.absoluteFill, animatedStyles]}>
        <Pressable
          style={[
            StyleSheet.absoluteFill,
            {
              alignItems: 'center',
              backgroundColor: theme.colors.backdrop,
              justifyContent: 'center',
            },
          ]}
          onPress={closePreviewImage}
        >
          <View
            style={{
              shadowColor: 'black',
              shadowOffset: { width: 0, height: 0 },
              shadowOpacity: 0.2,
              shadowRadius: 50,
              backgroundColor: 'white',
            }}
          >
            <Image
              source={{ uri: image.image }}
              cachePolicy="disk"
              onLoad={handleLoaded}
              style={{
                height: height || 1,
                width: width || 1,
              }}
            />
          </View>
        </Pressable>
      </Animated.View>
    </ScreenOverlay>
  );
};
